/**
 * Zustand Global Store — Single source of truth for all application state.
 *
 * Replaces the useState-based useAppStore hook with a module-level singleton.
 * All consumers share the same state instance — no more split-state bugs.
 *
 * Architecture:
 * - Pure functions from accounts/logic/ are UNCHANGED
 * - This store wraps them in set() calls via domain-specific action factories
 * - useAppStore.ts becomes a thin backward-compatible wrapper
 *
 * Split into domain modules as part of T1 (store.ts opsplitsen):
 * - createStore.ts    — store factory
 * - types.ts          — AppStoreState interface
 * - helpers.ts        — deriveSelectedGroup, removeDemoAccounts
 * - settingsActions.ts — initialize, group, period, dev/demo
 * - accountActions.ts  — account CRUD, relationships, classrooms
 * - learnerActions.ts  — recordAnswer, XP, streaks, mastery
 */

import { DEFAULT_GROUP } from '../../config/constants';
import { createStore } from './createStore';
import { schedulePersist, flushPersist } from './persistScheduler';
import type { AppStoreState } from './types';
import { createSettingsActions } from './settingsActions';
import { createAccountActions } from './accountActions';
import { createLearnerActions } from './learnerActions';

// ── Store Creation ───────────────────────────────────────────────────

export const useStore = createStore<AppStoreState>((set, get) => ({
  // ── Initial State ──
  multiAccountState: null,
  isInitialized: false,
  viewingLearnerId: null,
  selectedGroup: DEFAULT_GROUP,
  manualPeriod: null,
  groupVersion: 0,
  // ── Domain Actions ──
  ...createSettingsActions(set, get),
  ...createAccountActions(set, get),
  ...createLearnerActions(set, get)
}));

// ── Auto-persist multiAccountState on changes ────────────────────────
// W20: debounced + coalesced via persistScheduler (was a synchronous full-state
// write on every mutation, incl. the recordAnswer hot path). A trailing write
// collapses answer-streaks into one write; beforeunload/pagehide flush ensures
// nothing pending is lost on tab close. Critical one-shot writes (boot/reset/
// demo) still go through saveState directly in settingsActions.ts.
useStore.subscribe((state, prevState) => {
  if (state.isInitialized && state.multiAccountState && state.multiAccountState !== prevState.multiAccountState) {
    schedulePersist(state.multiAccountState);
  }
});

// ── Re-exports ───────────────────────────────────────────────────────
export type { AppStoreState } from './types';
export type { SetFn, GetFn } from './types';
export { deriveSelectedGroup } from './helpers';
export { flushPersist, subscribePersistFailure } from './persistScheduler';